import { z } from 'zod'
import { LOCATION_LEVELS } from '../domain/location-policy'
import {
  DOCUMENT_CLASSES,
  DocumentPolicyPatchSchema,
  DocumentPolicySchema,
  DocumentProviderIdSchema,
  Iso3Schema,
  PROVIDER_VALIDATION_STATES,
} from '../domain/document-policy'
import { IsoDateTimeSchema } from '../primitives'
import { route } from '../route-def'

/**
 * Admin document policy (identity-document spec §6.1 policy, §3.2 selection, §7 catalogue and processing matrix,
 * §1 R-D2 assurance matrix). Country codes, provider ids, classes, levels and reason codes only: nothing personal.
 */

export const DocumentClassSchema = z.enum(DOCUMENT_CLASSES)

/** One row of the assurance matrix (spec §1 R-D2): added only by a reviewed code change, never by an admin. */
export const AssuranceRowSchema = z.object({
  documentClass: z.string(),
  issuer: z.string(),
  provider: z.string(),
  captureMode: z.literal('hosted'),
  livenessProvider: z.string(),
  livenessMode: z.string().nullable(),
  bandsVersion: z.string(),
  /** The decisive liveness score must be at least this (independent of the admin-editable pass line). */
  minLivenessScore: z.number(),
  /** The document face score (2 decimals, floored) must be at least this. */
  minFaceScore: z.number(),
  evaluationRecord: z.string(),
})

export const AdminDocumentPolicyProviderSchema = z.object({
  id: DocumentProviderIdSchema,
  displayName: z.string(),
  effective: z.boolean(),
  validation: z.enum(PROVIDER_VALIDATION_STATES),
  /** Countries the provider's coverage (static list or stored catalogue snapshot) includes. */
  coverageCount: z.number().int().min(0),
  /** When the stored catalogue snapshot was fetched; null for a static list or before the first refresh. */
  catalogueFetchedAt: IsoDateTimeSchema.nullable(),
  /** The coverage is a list committed with the adapter (no refresh). */
  staticCatalogue: z.boolean(),
})

export const AdminDocumentPolicyCountrySchema = z.object({
  iso2: z.string().regex(/^[A-Z]{2}$/),
  iso3: Iso3Schema,
  label: z.string(),
  /** Processing matrix: ON unless listed in `countriesOff`. */
  on: z.boolean(),
  offReason: z.string().nullable(),
  /** The provider assigned to the country (its override, else the default). */
  provider: DocumentProviderIdSchema,
  exemption: z.object({ level: z.enum(LOCATION_LEVELS) }).nullable(),
  /** Classes each registered document provider covers for this country (catalogue, before the policy). */
  coverage: z.record(z.string(), z.array(DocumentClassSchema)),
})

export const AdminDocumentPolicyResponseSchema = z.object({
  policy: DocumentPolicySchema,
  providers: z.array(AdminDocumentPolicyProviderSchema),
  /** ISR and PSE first, then by label. */
  countries: z.array(AdminDocumentPolicyCountrySchema),
  /** Launch classes, global in the PoC (spec §7). */
  classes: z.array(DocumentClassSchema),
  /** Whether "Require an identity document" can be on: every provider in use is effective (validation state is informational). */
  canRequire: z.boolean(),
  /** `<provider id>: <reason>` for each provider in use that is not effective (never its validation state alone). */
  whyNot: z.array(z.string()),
  /** Read-only, empty at launch (every registration where the requirement applied ends in review). */
  assurance: z.array(AssuranceRowSchema),
})
export type AdminDocumentPolicyResponse = z.infer<typeof AdminDocumentPolicyResponseSchema>

export const DocumentCatalogueParamsSchema = z.object({ provider: DocumentProviderIdSchema })

export const documentsAdminRoutes = {
  adminDocumentPolicy: route({
    method: 'GET',
    path: '/api/admin/documents/policy',
    auth: 'admin',
    response: AdminDocumentPolicyResponseSchema,
  }),
  adminDocumentPolicyUpdate: route({
    method: 'PATCH',
    path: '/api/admin/documents/policy',
    auth: 'admin',
    body: DocumentPolicyPatchSchema,
    response: AdminDocumentPolicyResponseSchema,
  }),
  adminDocumentCatalogueRefresh: route({
    method: 'POST',
    path: '/api/admin/documents/catalogue/:provider/refresh',
    auth: 'admin',
    params: DocumentCatalogueParamsSchema,
    response: AdminDocumentPolicyResponseSchema,
  }),
}
